import React, { useEffect, useState, useMemo, useRef } from 'react';
import useDebounce from 'utils/hooks/debounce-hook';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { ScrollArea } from 'app/components/ui/scroll-area';
import { Button } from 'app/components/ui/button';
import { useMediaSlice } from 'app/pages/Media/slice';
import { useFormContext } from 'react-hook-form';
import { toast } from 'sonner';
import { useProductsSlice } from '../../slice';
import { Upload, X, Search, ChevronDown, Check } from 'lucide-react';
import { mediaBaseUrl } from 'utils/api/endpoints';
import { CategoryTree } from 'types';

export function CategoriesForm() {
  const form = useFormContext();
  const [isUploading, setIsUploading] = useState(false);
  const [parentSearchTerm, setParentSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debouncedSearchTerm = useDebounce(parentSearchTerm, 300);
  const [allCategoriesCache, setAllCategoriesCache] = useState<any[]>([]);

  const { useLazyGetProductsCategoriesQuery } = useProductsSlice();
  const [
    getCategories,
    {
      data: categoriesData,
      error: categoriesError,
      isLoading: categoriesLoading,
    },
  ] = useLazyGetProductsCategoriesQuery();

  const { useUploadMediaMutation } = useMediaSlice();
  const [uploadFile] = useUploadMediaMutation();

  // Load categories on component mount and when search changes
  useEffect(() => {
    getCategories({
      page: 1,
      search: debouncedSearchTerm,
      includeSubcategories: true,
    });
  }, [getCategories, debouncedSearchTerm]);

  // Cache all categories when we load them initially (empty search)
  useEffect(() => {
    if (categoriesData?.response?.data && !debouncedSearchTerm) {
      setAllCategoriesCache(categoriesData.response.data);
    }
  }, [categoriesData?.response?.data, debouncedSearchTerm]);

  // Handle category name change and auto-generate slug
  const handleCategoryNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const categoryName = e.target.value;
    form.setValue('category_name', categoryName);

    // Auto-generate slug from category name
    if (categoryName) {
      const slug = categoryName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single
        .trim();
      form.setValue('slug', slug);
    }
  };

  // Handle image upload
  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('products', file);

    try {
      const response = await uploadFile(formData).unwrap();
      const imageUrl = response?.response?.products?.[0];

      if (imageUrl) {
        form.setValue('image', imageUrl);
        toast.success('Image uploaded successfully');
      } else {
        throw new Error('No image URL returned');
      }
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  // Handle image removal
  const handleRemoveImage = () => {
    form.setValue('image', '');
    toast.success('Image removed');
  };

  // Handle parent category selection
  const handleParentCategoryChange = (value: string) => {
    form.setValue('parent_category_id', value);
    setIsDropdownOpen(false);
    setParentSearchTerm('');
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Convert flat categories to options with paths
  const convertCategoriesToOptions = (
    categories: any[],
  ): Array<{
    label: string;
    value: string;
    path: string;
    searchText: string;
  }> => {
    return categories.map(category => {
      const path = category.path || category.category_name;
      const searchText = `${category.category_name} ${path}`.toLowerCase();

      return {
        label: category.category_name, // Remove indentation, show clean name
        value: category.category_id.toString(),
        path: path, // Show full path without truncation
        searchText,
      };
    });
  };

  // Prepare parent category options with search
  const allParentOptions = useMemo(
    () => [
      {
        label: 'No Parent (Root Level)',
        value: '0',
        path: 'Root Level',
        searchText: 'no parent root level',
      },
      ...(categoriesData?.response?.data
        ? convertCategoriesToOptions(categoriesData.response.data)
        : []),
    ],
    [categoriesData?.response?.data],
  );

  // Keep a separate copy of all options for selected category display using cached data
  const allCategoriesForDisplay = useMemo(() => {
    const cachedOptions =
      allCategoriesCache.length > 0
        ? convertCategoriesToOptions(allCategoriesCache)
        : convertCategoriesToOptions(categoriesData?.response?.data || []);

    return [
      {
        label: 'No Parent (Root Level)',
        value: '0',
        path: 'Root Level',
        searchText: 'no parent root level',
      },
      ...cachedOptions,
    ];
  }, [allCategoriesCache, categoriesData?.response?.data]);

  // Filter options based on search term (API-driven filtering)
  const filteredParentOptions = useMemo(() => {
    // Since we're using API search, we don't need client-side filtering
    // The API already returns filtered results based on debouncedSearchTerm
    return allParentOptions;
  }, [allParentOptions]);

  // Handle errors
  useEffect(() => {
    if (categoriesError) {
      toast.error('Failed to load categories. Please refresh the page.');
    }
  }, [categoriesError]);

  const currentImage = form.watch('image');

  return (
    <ScrollArea className="h-[400px]">
      <div className="px-4 space-y-6">
        {/* Category Name */}
        <div className="space-y-2">
          <Label htmlFor="category_name" className="text-sm font-medium">
            Category Name *
          </Label>
          <Input
            id="category_name"
            placeholder="Enter category name"
            {...form.register('category_name', {
              required: 'Category name is required',
              minLength: {
                value: 2,
                message: 'Category name must be at least 2 characters',
              },
              maxLength: {
                value: 100,
                message: 'Category name must be less than 100 characters',
              },
            })}
            onChange={handleCategoryNameChange}
            className="w-full"
          />
          {form.formState.errors.category_name && (
            <p className="text-sm text-red-500">
              {String(form.formState.errors.category_name.message)}
            </p>
          )}
        </div>

        {/* Slug */}
        <div className="space-y-2">
          <Label htmlFor="slug" className="text-sm font-medium">
            Slug *
          </Label>
          <Input
            id="slug"
            placeholder="category-slug"
            {...form.register('slug', {
              required: 'Slug is required',
              pattern: {
                value: /^[a-z0-9-]+$/,
                message:
                  'Slug can only contain lowercase letters, numbers, and hyphens',
              },
              minLength: {
                value: 2,
                message: 'Slug must be at least 2 characters',
              },
            })}
            className="w-full"
          />
          {form.formState.errors.slug && (
            <p className="text-sm text-red-500">
              {String(form.formState.errors.slug.message)}
            </p>
          )}
          <p className="text-xs text-gray-500">
            URL-friendly version of the category name
          </p>
        </div>

        {/* Parent Category */}
        <div className="space-y-2">
          <Label htmlFor="parent_category_id" className="text-sm font-medium">
            Parent Category
          </Label>

          {/* Custom Parent Category Dropdown */}
          <div className="relative" ref={dropdownRef}>
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search categories..."
                value={parentSearchTerm}
                onChange={e => {
                  setParentSearchTerm(e.target.value);
                  setIsDropdownOpen(true);
                }}
                onFocus={() => setIsDropdownOpen(true)}
                className="pl-10 pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''
                    }`}
                />
              </Button>
            </div>

            {/* Custom Dropdown */}
            {isDropdownOpen && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                {categoriesLoading ? (
                  <div className="p-3 text-sm text-gray-500 text-center">
                    Loading categories...
                  </div>
                ) : filteredParentOptions.length > 0 ? (
                  <div className="py-1">
                    {filteredParentOptions.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none flex items-center justify-between"
                        onClick={() => handleParentCategoryChange(option.value)}
                      >
                        <div className="flex flex-col">
                          <span className="font-medium">{option.label}</span>
                          <span className="text-xs text-gray-500">
                            {option.path}
                          </span>
                        </div>
                        {form.watch('parent_category_id') === option.value && (
                          <Check className="h-4 w-4 text-blue-600" />
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="p-3 text-xs text-gray-500 text-center">
                    No categories found
                  </div>
                )}
              </div>
            )}

            {/* Selected Category Display */}
            {form.watch('parent_category_id') &&
              form.watch('parent_category_id') !== '0' && (
                <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-md">
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    <span className="font-medium">Selected:</span>{' '}
                    {allCategoriesForDisplay.find(
                      opt => opt.value === form.watch('parent_category_id'),
                    )?.label || 'Unknown Category'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    <span className="font-medium">Path:</span>{' '}
                    {allCategoriesForDisplay.find(
                      opt => opt.value === form.watch('parent_category_id'),
                    )?.path || 'Path not available'}
                  </p>
                </div>
              )}
          </div>

          <p className="text-xs text-gray-500">
            Choose a parent category to create a hierarchy. Use search to find
            categories quickly.
          </p>
        </div>

        {/* Image Upload */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Category Image</Label>

          {/* Upload Area */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              id="image-upload"
              disabled={isUploading}
            />
            <label htmlFor="image-upload" className="cursor-pointer">
              <div className="flex flex-col items-center space-y-2">
                {isUploading ? (
                  <>
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="text-sm text-gray-500">Uploading...</p>
                  </>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Click to upload image
                      </p>
                      <p className="text-xs text-gray-500">
                        PNG, JPG, GIF up to 5MB
                      </p>
                    </div>
                  </>
                )}
              </div>
            </label>
          </div>

          {/* Image Preview */}
          {currentImage && (
            <div className="relative inline-block">
              <div className="relative w-32 h-32 border rounded-lg overflow-hidden">
                <img
                  src={`${mediaBaseUrl}/products/${currentImage}`}
                  alt="Category preview"
                  className="w-full h-full object-cover"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="absolute top-1 right-1 h-6 w-6 p-0"
                  onClick={handleRemoveImage}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </ScrollArea>
  );
}
